import { useState } from 'react';
import { X, AlertTriangle, Lock, Package, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { pagosCreditoApi } from '../lib/api';

interface DeudaAntiguaModalProps {
  bus: any;
  onClose: () => void;
  onAgregada: (padron: string, nuevoSaldo: number, nuevoSaldoProductos: number, nuevoSaldoServicios: number) => void;
}

const DeudaAntiguaModal = ({ bus, onClose, onAgregada }: DeudaAntiguaModalProps) => {
  const deudaActual = parseFloat(bus.credito_usado || 0);
  const [clave, setClave] = useState('');
  const [montoProductos, setMontoProductos] = useState('');
  const [montoServicios, setMontoServicios] = useState('');
  const [motivo, setMotivo] = useState('');
  const [procesando, setProcesando] = useState(false);

  const numProductos = parseFloat(montoProductos) || 0;
  const numServicios = parseFloat(montoServicios) || 0;
  const montoTotal = numProductos + numServicios;
  const montoValido = montoTotal > 0;

  const confirmar = async () => {
    if (!clave.trim()) {
      toast.error('Ingresa la clave de administrador');
      return;
    }
    if (!montoValido) {
      toast.error('Ingresa al menos un monto (productos o servicios)');
      return;
    }
    setProcesando(true);
    try {
      const resultado = await pagosCreditoApi.agregarDeudaAntigua(bus.padron, {
        clave: clave.trim(),
        monto_productos: numProductos,
        monto_servicios: numServicios,
        motivo: motivo.trim() || undefined,
      });
      toast.success(`Deuda antigua de S/ ${montoTotal.toFixed(2)} agregada`);
      onAgregada(bus.padron, resultado.credito_usado, resultado.credito_usado_productos, resultado.credito_usado_servicios);
      onClose();
    } catch (error: any) {
      toast.error('Error al agregar la deuda: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
    >
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="text-orange-500" size={20} />
            Deuda antigua — Bus {bus.padron}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
            Para deuda de este bus que no quedó registrada por productos ni por
            servicios (arrastrada de antes). Puedes cargar uno de los dos montos, o
            los dos a la vez si la deuda incluye ambos — quedan sumados al saldo del
            bus pero guardados por separado en Auditoría. No crea ninguna venta.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <Package size={14} className="text-gray-500" /> Deuda por productos (S/)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={montoProductos}
                onChange={(e) => setMontoProductos(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <Wrench size={14} className="text-gray-500" /> Deuda por servicios (S/)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={montoServicios}
                onChange={(e) => setMontoServicios(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Motivo / referencia (opcional)</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              rows={2}
              placeholder="Ej: deuda arrastrada del sistema anterior"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-1">
              <Lock size={14} /> Clave de administrador
            </label>
            <input
              type="password"
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="****"
            />
          </div>

          <div className="border-t pt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Deuda actual</span>
              <span className="font-bold">S/ {deudaActual.toFixed(2)}</span>
            </div>
            {numProductos > 0 && (
              <div className="flex justify-between text-xs text-gray-500">
                <span>+ Productos</span>
                <span>S/ {numProductos.toFixed(2)}</span>
              </div>
            )}
            {numServicios > 0 && (
              <div className="flex justify-between text-xs text-gray-500">
                <span>+ Servicios</span>
                <span>S/ {numServicios.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Deuda después de agregar</span>
              <span className="font-bold text-orange-600">
                S/ {(deudaActual + montoTotal).toFixed(2)}
              </span>
            </div>
          </div>

          <button
            onClick={confirmar}
            disabled={procesando || !montoValido}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <AlertTriangle size={18} />
            {procesando ? 'Procesando...' : `Agregar deuda de S/ ${montoTotal.toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeudaAntiguaModal;
